import {
  cloneDeep,
  dropRight,
  get,
  initial,
  last,
  set,
  without,
} from "lodash-es"
import { ActiveMove, MovesHistory } from "@/types/types"
import { Move } from "chess.js"

export function getPath(path: ActiveMove, key = "moves") {
  if (!path || path.length === 0) return []

  return path.reduce(
    (a, b, i) => {
      if (i !== 0) {
        a.push(key)
      }
      return a.concat(b)
    },
    [] as (number | string)[]
  )
}

export function getPathToCurrentLine(position: ActiveMove) {
  return dropRight(getPath(position), 1)
}

export function getCurrentLine(moves: MovesHistory, position: ActiveMove) {
  return get(moves, getPathToCurrentLine(position)) || []
}

export function getPathToCurrentMove(position: ActiveMove) {
  return getPath(position)
}

export function getPathToParentMove(position: ActiveMove) {
  return dropRight(getPath(position), 3)
}

export function getPathToSiblingLines(position: ActiveMove) {
  return dropRight(getPath(position), 2)
}

export function getPathToChildLines(position: ActiveMove) {
  return getPath(position).concat("moves")
}

export function getCurrentMoveIndex(position: ActiveMove) {
  return last(getPath(position))
}

export function getCurrentLineIndex(position: ActiveMove): number {
  const lastElement = last(position)
  return lastElement ? lastElement[0] : 0
}

export function getNewLinePosition(moves: MovesHistory, position: ActiveMove) {
  return position.concat([[getChildLines(moves, position).length, 0]])
}

export function getPreviousMovePosition(
  moves: MovesHistory,
  position: ActiveMove
) {
  if (!position) return []

  const currentMoveIndex = getCurrentMoveIndex(position)
  if (typeof currentMoveIndex !== "number") return []

  const previousPosition = set(
    cloneDeep(position),
    [position.length - 1, 1],
    currentMoveIndex - 1
  )

  return getFirstValidPosition(moves, previousPosition)
}

export function getNextMovePosition(
  moves: MovesHistory,
  position: ActiveMove
): ActiveMove | undefined {
  if (!position) return []

  if (moves?.length > 0 && position.length === 0) return [[0, 0]]

  const currentMoveIndex = getCurrentMoveIndex(position)
  if (typeof currentMoveIndex !== "number") return undefined

  const nextPosition = set(
    cloneDeep(position),
    [position.length - 1, 1],
    currentMoveIndex + 1
  )

  if (get(moves, getPath(nextPosition))) {
    return nextPosition
  }

  return undefined
}

export function incrementMovePosition(position: ActiveMove) {
  const currentMoveIndex = getCurrentMoveIndex(position)
  if (typeof currentMoveIndex !== "number") return []

  return set(
    cloneDeep(position),
    [position.length - 1, 1],
    currentMoveIndex + 1
  )
}

export function getParentMovePosition(position: ActiveMove) {
  return initial(position)
}

export function decrementLinePosition(position: ActiveMove) {
  return set(
    cloneDeep(position),
    [position.length - 1, 0],
    getCurrentLineIndex(position) - 1
  )
}

export function incrementLinePosition(position: ActiveMove) {
  return set(
    cloneDeep(position),
    [position.length - 1, 0],
    getCurrentLineIndex(position) + 1
  )
}

export function getFirstValidPosition(
  moves: MovesHistory,
  position: ActiveMove
) {
  if (position.length === 0) {
    return position
  }

  const pos = [...position]
  while (pos.length > 0) {
    const linePos = pos.pop()
    if (!linePos) return []
    const lineIndex = linePos[0]
    let moveIndex = linePos[1]
    while (moveIndex >= 0) {
      const movePos = pos.concat([[lineIndex, moveIndex]])
      if (get(moves, getPath(movePos))) {
        return movePos
      }
      moveIndex--
    }
  }

  return []
}

export function findMatchingNextPosition(
  moves: MovesHistory,
  position: ActiveMove,
  move: Move
) {
  const nextMovePosition = getNextMovePosition(moves, position)
  if (!nextMovePosition) return undefined

  const nextMove = get(moves, getPath(nextMovePosition))

  if (nextMove && nextMove.san === move.san) {
    return nextMovePosition
  }

  const childLines = getChildLines(moves, position)
  for (let i = 0; i < childLines.length; i++) {
    const childLine = childLines[i]
    if (childLine[0].san === move.san) {
      return position.concat([[i, 0]])
    }
  }
}

export function getCurrentMove(moves: MovesHistory, position: ActiveMove) {
  return get(moves, getPathToCurrentMove(position))
}

export function getParentMove(moves: MovesHistory, position: ActiveMove) {
  return get(moves, getPathToParentMove(position))
}

export function getChildLines(moves: MovesHistory, position: ActiveMove) {
  if (position.length === 0) {
    return moves
  }
  return get(moves, getPathToChildLines(position)) || []
}

export function getSiblingLines(moves: MovesHistory, position: ActiveMove) {
  if (position.length === 1) {
    return moves
  } else {
    return get(moves, getPathToSiblingLines(position))
  }
}

export function appendMoveToCurrentLine(
  moves: MovesHistory,
  position: ActiveMove,
  move: Move
) {
  return set(
    cloneDeep(moves),
    getPathToCurrentLine(position),
    getCurrentLine(moves, position).concat([move])
  )
}

export function appendLineToCurrentMove(
  moves: MovesHistory,
  position: ActiveMove,
  line: any[]
) {
  if (position.length > 0) {
    return set(
      cloneDeep(moves),
      getPathToChildLines(position),
      getChildLines(moves, position).concat([line])
    )
  } else {
    return cloneDeep(moves).concat([line])
  }
}

export function removeCurrentLine(moves: MovesHistory, position: ActiveMove) {
  const newLines = without(
    getSiblingLines(moves, position),
    getCurrentLine(moves, position)
  )
  if (position.length === 1) {
    return cloneDeep(newLines)
  } else {
    return set(cloneDeep(moves), getPathToSiblingLines(position), newLines)
  }
}

export function replaceCurrentLine(
  moves: MovesHistory,
  position: ActiveMove,
  line: any[]
) {
  return set(cloneDeep(moves), getPathToCurrentLine(position), line)
}

export function removeCurrentMove(moves: MovesHistory, position: ActiveMove) {
  return set(
    cloneDeep(moves),
    getPathToCurrentLine(position),
    initial(getCurrentLine(moves, position))
  )
}

export function removeLine(
  moves: MovesHistory,
  position: ActiveMove,
  lineIndex: number
) {
  const lines = getChildLines(moves, position)
  return set(cloneDeep(moves), getPathToChildLines(position), [
    ...lines.slice(0, lineIndex),
    ...lines.slice(lineIndex + 1, lines.length),
  ])
}

export function updateCommentForCurrentMove(
  moves: MovesHistory,
  position: ActiveMove,
  comment: string
) {
  return set(
    cloneDeep(moves),
    getPathToCurrentMove(position).concat("comment"),
    comment
  )
}
export function updateAnnotationForCurrentMove(
  moves: MovesHistory,
  position: ActiveMove,
  annotation: string
) {
  return set(
    cloneDeep(moves),
    getPathToCurrentMove(position).concat("annotation"),
    annotation
  )
}

export function atFirstMoveOnCurrentLine(position: ActiveMove) {
  return getCurrentMoveIndex(position) === 0
}

export function atLastMoveOnCurrentLine(
  moves: MovesHistory,
  position: ActiveMove
) {
  return (
    getCurrentMoveIndex(position) === getCurrentLine(moves, position).length - 1
  )
}

export function atMainLine(position: ActiveMove) {
  return position.length === 1
}

export function atFirstLine(position: ActiveMove) {
  return getCurrentLineIndex(position) === 0
}

export function atLastLine(moves: MovesHistory, position: ActiveMove) {
  return (
    getCurrentLineIndex(position) ===
    getSiblingLines(moves, position).length - 1
  )
}
